import getMonth from 'date-fns/getMonth';
import getYear from 'date-fns/getYear';
import { ValidateResult } from '../functions/ux/form/useForm';
import { US_STATES } from '../models/ClientModels';

export const errorMessage = {
  email: 'Invalid email format',
  username:
    'User name can only contain letters (a-z, A-Z), numbers (0-9), underscore (_), and periods (.) ',
  phone: 'Invalid phone format',
  dateFormat: 'Invalid Date format',
  cvv: 'Invalid CVV',
  httpUrl: 'Invalid URL format',
  state: 'Invalid State',
  decimal: 'Invalid Decimal',
  integer: 'Invalid Number',
};

export const kUserNameLengthLimit = { min: 3, max: 16 };

export const validateUserName = (username: string): ValidateResult => {
  username = username.trim();
  if (!username) {
    return { type: 'invalid', msg: 'User name is required' };
  }

  const { min, max } = kUserNameLengthLimit;

  if (username.length < min) {
    return {
      type: 'invalid',
      msg: `User name must be at least ${min} characters`,
    };
  }

  if (username.length > max) {
    return {
      type: 'invalid',
      msg: `User name must be at most ${max} characters`,
    };
  }

  const pattern = /^[A-Z0-9_\.]+$/i;

  return pattern.test(username)
    ? { type: 'valid' }
    : { type: 'invalid', msg: errorMessage.username };
};

export const validateEmail = (email: string): ValidateResult => {
  const regexp =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  return regexp.test(email)
    ? { type: 'valid' }
    : { type: 'invalid', msg: errorMessage.email };
};

export const validateUSPhone = (phone: string): ValidateResult => {
  const regexp =
    /^(\+1)?[-.]?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;

  return regexp.test(phone)
    ? { type: 'valid' }
    : { type: 'invalid', msg: errorMessage.phone };
};

export const validateGenericPhone = (input: string): ValidateResult => {
  const res = validateInteger(input.replace(/[+-]/g, ''));
  if (res.type === 'invalid') {
    res.msg = errorMessage.phone;
  }
  return res;
};

export const clearPhoneFormat = (phone: string) => {
  return phone.replace(/[()\- ]/g, '');
};

/**
 * Validate if expDate has expired
 * @param expDate format `MM-YY`
 * @param now current datetime
 */
export const validateExpDate = (
  expDate: string,
  _: any,
  now = new Date(),
): ValidateResult => {
  const currentMonth = zeroBasedToOneBasedNumbering(getMonth(now));
  const currentYear = getYear(now) % 100;
  const month = parseInt(expDate.substring(0, 2), 10);
  const year = parseInt(expDate.substring(3), 10);

  if (month > 12) {
    return { type: 'invalid', msg: errorMessage.dateFormat };
  }

  if (
    year > currentYear ||
    (year === currentYear && month >= currentMonth && month <= 12)
  ) {
    return { type: 'valid' };
  }

  return { type: 'invalid', msg: errorMessage.dateFormat };

  function zeroBasedToOneBasedNumbering(n: number): number {
    return n + 1;
  }
};

export const validateCVV = (cvv: string): ValidateResult => {
  const regex = /[0-9]{3,4}/;

  return regex.test(cvv)
    ? { type: 'valid' }
    : { type: 'invalid', msg: errorMessage.cvv };
};

export const validateUSState = (state: string): ValidateResult => {
  if (US_STATES.includes(state)) {
    return { type: 'valid' };
  } else {
    return { type: 'invalid', msg: errorMessage.state };
  }
};

export const validateDecimal =
  (precision = 2) =>
  (input: string): ValidateResult => {
    const parsed = parseFloat(input);

    if (Number.isNaN(parsed)) {
      return { type: 'invalid', msg: errorMessage.decimal };
    }

    const fixedNumber = parsed.toFixed(precision);
    if (Number(fixedNumber) !== Number(input)) {
      return { type: 'invalid', msg: errorMessage.decimal };
    }

    return { type: 'valid' };
  };

export const validateInteger = (input: string): ValidateResult => {
  const parsed = parseInt(input, 10);

  const isDecimal = Number(input) % 1 !== 0;

  if (Number.isNaN(parsed) || isDecimal) {
    return { type: 'invalid', msg: errorMessage.integer };
  }

  return { type: 'valid' };
};

export const validateLength =
  (operator: '>' | '<' | '=', length: number) =>
  (input: string): ValidateResult => {
    switch (operator) {
      case '>':
        if (input.length < length) {
          return {
            type: 'invalid',
            msg: `Should more than ${length} characters`,
          };
        }
        break;
      case '<':
        if (input.length > length) {
          return {
            type: 'invalid',
            msg: `Should less than ${length} characters`,
          };
        }
        break;
      case '=':
        if (input.length !== length) {
          return { type: 'invalid', msg: `Should input ${length} characters` };
        }
        break;
    }

    return { type: 'valid' };
  };

export function validNumber(input: any): ValidateResult {
  if (typeof input === 'number' && !Number.isNaN(input)) {
    return { type: 'valid' };
  }
  return {
    type: 'invalid',
    msg: 'This field must be a number',
  };
}

export function positiveNumber(input: any): ValidateResult {
  if (typeof input === 'number' && !Number.isNaN(input) && input >= 0) {
    return { type: 'valid' };
  }
  return {
    type: 'invalid',
    msg: 'Positive number only',
  };
}

export function priceNumber(input: any): ValidateResult {
  if (!(typeof input === 'number' && !Number.isNaN(input) && input >= 0)) {
    return {
      type: 'invalid',
      msg: 'Positive number only',
    };
  }

  const rex = /^\d+(\.\d{1,2})?$/;
  if (typeof input === 'number' && !rex.test(input as any)) {
    return {
      type: 'invalid',
      msg: 'The price must be a number with a maximum of two decimal places',
    };
  }

  return { type: 'valid' };
}
